import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Blocks, ArrowLeft, MessageSquare, Webhook, Key, Plus, Power, Save, Loader2, Shield, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/lib/toast';
import { integrationsApi } from '@/api/integrationsApi';

export function IntegrationsPage({ 
  onBack, 
  initialTab = 'teams',
}: { 
  onBack: () => void, 
  initialTab?: string,
}) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [msTeamsWebhook, setMsTeamsWebhook] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [isCreatingWebhook, setIsCreatingWebhook] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookEvents, setNewWebhookEvents] = useState('ticket.created, ticket.updated');
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');

  const { user } = useAuth();
  const { push: toast } = useToast();
  const navigate = useNavigate();

  const userRole = (user?.role || '').toLowerCase().replace(/[-_]/g, ' ').trim();
  const isSuperAdmin = userRole === 'super admin' || userRole === 'superadmin';

  const loadIntegrations = async () => {
    try {
      try {
        const list = await integrationsApi.getIntegrations(user?.role === 'Super Admin' ? 'ALL' : (user?.organizationId || undefined));
        if (list && Array.isArray(list)) {
          const teamsData = list.find((i: any) => i.provider === 'msteams');
          if (teamsData && teamsData.config?.webhook_url) {
            setMsTeamsWebhook(teamsData.config.webhook_url);
          }
          setWebhooks(list.filter((i: any) => i.provider === 'webhook'));
          setApiKeys(list.filter((i: any) => i.provider === 'api_key'));
          return;
        }
      } catch (apiErr) {
        // Fallback
      }

      if (!supabase) return;
      // 1. Teams
      const { data: teamsData } = await supabase
        .from('integrations')
        .select('*')
        .eq('provider', 'msteams')
        .maybeSingle();
      if (teamsData && teamsData.config?.webhook_url) {
        setMsTeamsWebhook(teamsData.config.webhook_url);
      }

      // 2. Webhooks
      const { data: whData } = await supabase
        .from('integrations')
        .select('*')
        .eq('provider', 'webhook')
        .order('created_at', { ascending: false });
      if (whData) setWebhooks(whData);

      // 3. API Keys
      const { data: keysData } = await supabase
        .from('integrations')
        .select('*')
        .eq('provider', 'api_key')
        .order('created_at', { ascending: false });
      if (keysData) setApiKeys(keysData);
    } catch (err) {
      console.error('Failed to load integrations:', err);
    }
  };

  useEffect(() => {
    loadIntegrations();
  }, []);

  const handleSaveWebhook = async () => {
    setIsSaving(true);
    try {
      const orgId = user?.organizationId || undefined;
      try {
        await integrationsApi.saveIntegration({
          provider: 'msteams',
          organization_id: orgId,
          config: { webhook_url: msTeamsWebhook.trim() },
          is_active: Boolean(msTeamsWebhook.trim())
        });
        await loadIntegrations();
        toast('Microsoft Teams Webhook URL saved successfully!');
        return;
      } catch (apiErr) {
        // Fallback
      }

      if (!supabase) return;
      const { data: existing } = await supabase
        .from('integrations')
        .select('id')
        .eq('provider', 'msteams')
        .maybeSingle();

      if (existing?.id) {
        const { error } = await supabase
          .from('integrations')
          .update({
            config: { webhook_url: msTeamsWebhook.trim() },
            is_active: Boolean(msTeamsWebhook.trim()),
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('integrations')
          .insert({
            organization_id: orgId,
            provider: 'msteams',
            config: { webhook_url: msTeamsWebhook.trim() },
            is_active: Boolean(msTeamsWebhook.trim()),
            created_at: new Date().toISOString()
          });
        if (error) throw error;
      }
      toast('Microsoft Teams Webhook URL saved successfully!');
    } catch (e: any) {
      console.error(e);
      toast('Failed to save webhook URL: ' + (e.message || 'Server error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddWebhook = async () => {
    if (!newWebhookUrl.trim()) return;
    try {
      const orgId = user?.organizationId || undefined;
      try {
        await integrationsApi.saveIntegration({
          provider: 'webhook',
          organization_id: orgId,
          config: { url: newWebhookUrl.trim(), events: newWebhookEvents.split(',').map(s => s.trim()) },
          is_active: true
        });
        setNewWebhookUrl('');
        setIsCreatingWebhook(false);
        await loadIntegrations();
        toast('Outgoing webhook added successfully!');
        return;
      } catch (apiErr) {
        // Fallback
      }

      if (!supabase) return;
      const { error } = await supabase.from('integrations').insert({
        organization_id: orgId,
        provider: 'webhook',
        config: { url: newWebhookUrl.trim(), events: newWebhookEvents.split(',').map(s => s.trim()) },
        is_active: true
      });
      if (error) throw error;
      setNewWebhookUrl('');
      setIsCreatingWebhook(false);
      await loadIntegrations();
      toast('Outgoing webhook added successfully!');
    } catch (e: any) {
      toast('Failed to add webhook: ' + e.message);
    }
  };

  const handleDeleteIntegration = async (id: string) => {
    try {
      try {
        await integrationsApi.deleteIntegration(id);
        await loadIntegrations();
        toast('Integration removed successfully');
        return;
      } catch (apiErr) {
        // Fallback
      }

      if (!supabase) return;
      const { error } = await supabase.from('integrations').delete().eq('id', id);
      if (error) throw error;
      await loadIntegrations();
      toast('Integration removed successfully');
    } catch (e: any) {
      toast('Failed to delete integration: ' + e.message);
    }
  };

  const handleGenerateApiKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const orgId = user?.organizationId || undefined;
      const randomKey = 'cmp_live_' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
      try {
        await integrationsApi.saveIntegration({
          provider: 'api_key',
          organization_id: orgId,
          config: { name: newKeyName.trim(), prefix: randomKey.substring(0, 14) + '...', full_key: randomKey },
          is_active: true
        });
        setNewKeyName('');
        setIsCreatingKey(false);
        await loadIntegrations();
        toast('API Key generated successfully');
        return;
      } catch (apiErr) {
        // Fallback
      }

      if (!supabase) return;
      const { error } = await supabase.from('integrations').insert({
        organization_id: orgId,
        provider: 'api_key',
        config: { name: newKeyName.trim(), prefix: randomKey.substring(0, 14) + '...', full_key: randomKey },
        is_active: true
      });
      if (error) throw error;
      setNewKeyName('');
      setIsCreatingKey(false);
      await loadIntegrations();
      toast('API Key generated successfully');
    } catch (e: any) {
      toast('Failed to generate key: ' + e.message);
    }
  };

  const renderTabContent = () => {
    
    switch (activeTab) {
      case 'sentinel':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <h3 className="font-display text-lg font-medium flex items-center gap-2">
                  <Shield className="h-5 w-5 text-indigo-500" />
                  Azure Sentinel SIEM Integration
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Automated security incident ingestion, duplicate prevention, and SOC team orchestration.
                </p>
              </div>
              <button
                onClick={() => navigate('/admin/integrations/sentinel')}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
              >
                Launch Full Settings
              </button>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-3 text-emerald-500 bg-emerald-500/10 p-3 rounded-xl border border-emerald-500/20 text-xs font-semibold">
                <ShieldCheck className="h-4 w-4" />
                <span>Superadmin Privileges Verified • Enterprise SIEM Pipeline Active</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Manage your Azure Tenant, Log Analytics Workspace credentials, automatic 15-stage workflow provision, severity mappings, and simulate live Sentinel threat alerts.
              </p>
              <div className="pt-2">
                <button
                  onClick={() => navigate('/admin/integrations/sentinel')}
                  className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-2 transition"
                >
                  Configure Microsoft Sentinel
                </button>
              </div>
            </div>
          </div>
        );
      case 'workspace':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <h3 className="font-display text-lg font-medium">Google Workspace</h3>
                <p className="text-sm text-muted-foreground mt-1">Integrate with Gmail and Google Drive for attachments.</p>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-12 text-center shadow-sm max-w-2xl mx-auto mt-8">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-500/10 text-orange-500 mb-4">
                <Power className="h-8 w-8" />
              </div>
              <h4 className="font-medium text-lg">Not Connected</h4>
              <p className="text-sm text-muted-foreground mt-2 mb-6 max-w-sm mx-auto">
                Connect your Google Workspace admin account to sync users, manage emails, and store ticket attachments in Drive.
              </p>
              <button className="inline-flex items-center gap-2 rounded-lg bg-foreground text-background px-6 py-2.5 text-sm font-medium transition hover:opacity-90">
                Sign in with Google
              </button>
            </div>
          </div>
        );
      case 'teams':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <h3 className="font-display text-lg font-medium">Microsoft Teams Integration</h3>
                <p className="text-sm text-muted-foreground mt-1">Send notifications and create tickets directly from Teams.</p>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card shadow-sm p-0 overflow-hidden mt-6">
              <div className="p-6 flex items-center justify-between bg-muted/20">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#464EB8]/10 text-[#464EB8]">
                    <MessageSquare className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-medium">Incoming Webhook (Connector)</h4>
                    <p className="text-sm text-muted-foreground">Post ticket updates into a Teams Channel</p>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-border space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Microsoft Teams Webhook URL</label>
                  <input 
                    value={msTeamsWebhook}
                    onChange={e => setMsTeamsWebhook(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                    placeholder="https://your-domain.webhook.office.com/webhookb2/..."
                  />
                  <p className="mt-2 text-xs text-muted-foreground">Paste the Webhook URL generated from the "Incoming Webhook" connector in your Microsoft Teams channel.</p>
                </div>
                
                <div className="flex justify-end pt-4">
                  <button 
                    onClick={handleSaveWebhook}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save Integration
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'webhooks':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <h3 className="font-display text-lg font-medium">Outgoing Webhooks</h3>
                <p className="text-sm text-muted-foreground mt-1">Push real-time ticket events to external services.</p>
              </div>
              <button 
                onClick={() => setIsCreatingWebhook(!isCreatingWebhook)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
              >
                <Plus className="h-4 w-4" /> New Endpoint
              </button>
            </div>

            {isCreatingWebhook && (
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h4 className="font-medium text-sm">Add Webhook Endpoint</h4>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Payload URL</label>
                  <input 
                    value={newWebhookUrl}
                    onChange={e => setNewWebhookUrl(e.target.value)}
                    placeholder="https://api.yourdomain.com/webhook"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Subscribed Events</label>
                  <input 
                    value={newWebhookEvents}
                    onChange={e => setNewWebhookEvents(e.target.value)}
                    placeholder="ticket.created, ticket.updated, ticket.closed"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button 
                    onClick={() => setIsCreatingWebhook(false)}
                    className="px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleAddWebhook}
                    className="px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90"
                  >
                    Create Endpoint
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Endpoint URL</th>
                    <th className="px-4 py-3 font-medium">Events</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {webhooks.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-sm">
                        No outgoing webhooks configured yet. Click "New Endpoint" to connect a service.
                      </td>
                    </tr>
                  ) : (
                    webhooks.map((wh) => (
                      <tr key={wh.id} className="bg-card">
                        <td className="px-4 py-4 font-mono text-xs">{wh.config?.url || '—'}</td>
                        <td className="px-4 py-4">
                          <span className="rounded bg-muted px-2 py-1 text-[10px]">
                            {Array.isArray(wh.config?.events) ? wh.config.events.join(', ') : 'ticket.all'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                            wh.is_active ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"
                          )}>
                            {wh.is_active ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button 
                            onClick={() => handleDeleteIntegration(wh.id)}
                            className="text-red-500 hover:underline text-xs font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'api':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <h3 className="font-display text-lg font-medium">API Keys</h3>
                <p className="text-sm text-muted-foreground mt-1">Manage API keys for custom integrations and scripting.</p>
              </div>
              <button 
                onClick={() => setIsCreatingKey(!isCreatingKey)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
              >
                <Plus className="h-4 w-4" /> Generate Key
              </button>
            </div>

            {isCreatingKey && (
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h4 className="font-medium text-sm">Generate New API Key</h4>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Key Name / Description</label>
                  <input 
                    value={newKeyName}
                    onChange={e => setNewKeyName(e.target.value)}
                    placeholder="e.g. Zapier Integration or CI/CD Pipeline"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button 
                    onClick={() => setIsCreatingKey(false)}
                    className="px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleGenerateApiKey}
                    className="px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90"
                  >
                    Generate
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Key Prefix</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {apiKeys.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-sm">
                        No active API keys found. Click "Generate Key" to create one.
                      </td>
                    </tr>
                  ) : (
                    apiKeys.map((k) => (
                      <tr key={k.id} className="bg-card">
                        <td className="px-4 py-4 font-medium text-foreground">{k.config?.name || 'API Key'}</td>
                        <td className="px-4 py-4 font-mono text-xs text-muted-foreground">{k.config?.prefix || 'cmp_live_...'}</td>
                        <td className="px-4 py-4">
                          <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-500">
                            Active
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button 
                            onClick={() => handleDeleteIntegration(k.id)}
                            className="text-red-500 hover:underline text-xs font-medium"
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-6 py-4">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500/10 text-teal-500">
          <Blocks className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-lg font-semibold tracking-tight">Integrations Hub</h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Connect your ticketing system with external services and platforms.
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
        <div className="w-full md:w-64 border-r border-border bg-muted/10 p-4 space-y-1 overflow-y-auto">
          {[
            { id: 'teams', label: 'Teams Integration', icon: MessageSquare },
            { id: 'webhooks', label: 'Webhooks', icon: Webhook },
            { id: 'api', label: 'API Keys', icon: Key },
            ...(isSuperAdmin ? [
              { id: 'sentinel', label: 'Azure Sentinel', icon: Shield }
            ] : [])
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition",
                activeTab === tab.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto bg-muted/5 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto w-full max-w-4xl">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
